import { useState, useMemo } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BarChart3, TrendingUp, TrendingDown, Briefcase, DollarSign } from "lucide-react";
import { useTransactions } from "@/hooks/useTransactions";
import { useCostCenters } from "@/hooks/useCostCenters";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePickerWithRange } from "@/components/DatePickerWithRange";
import { DateRange } from "react-day-picker";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface ProjectMetrics {
  costCenterId: number;
  costCenterName: string;
  revenue: number;
  expenses: number;
  profit: number;
  profitPercentage: number;
  isProfitable: boolean;
}

export function ProjectDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
  });

  const { transactions, isLoading: transactionsLoading } = useTransactions();
  const { costCenters, isLoading: costCentersLoading } = useCostCenters();

  // Calculate project metrics
  const projectMetrics = useMemo<ProjectMetrics[]>(() => {
    if (!transactions || !costCenters) return [];

    const filteredTransactions = transactions.filter((transaction) => {
      if (!transaction.cost_center_id) return false;
      if (!dateRange.from || !dateRange.to) return true;
      const transactionDate = new Date(transaction.date);
      return transactionDate >= dateRange.from && transactionDate <= dateRange.to;
    });

    // Group by cost center
    const metricsMap = new Map<number, ProjectMetrics>();

    filteredTransactions.forEach((transaction) => {
      if (!transaction.cost_center_id) return;

      const costCenterId = transaction.cost_center_id;
      const costCenter = costCenters.find((cc) => cc.id === costCenterId);
      const costCenterName = costCenter?.name || `Centro ${costCenterId}`;

      if (!metricsMap.has(costCenterId)) {
        metricsMap.set(costCenterId, {
          costCenterId,
          costCenterName,
          revenue: 0,
          expenses: 0,
          profit: 0,
          profitPercentage: 0,
          isProfitable: false,
        });
      }

      const metric = metricsMap.get(costCenterId)!;
      const amount = Number(transaction.amount) || 0;

      if (transaction.type === 'income' && transaction.is_paid) {
        metric.revenue += amount;
      } else if (transaction.type === 'expense' && transaction.is_paid) {
        metric.expenses += amount;
      }
    });

    // Calculate profit and percentages
    const metrics = Array.from(metricsMap.values()).map((metric) => {
      metric.profit = metric.revenue - metric.expenses;
      metric.profitPercentage = metric.revenue > 0 
        ? (metric.profit / metric.revenue) * 100 
        : 0;
      metric.isProfitable = metric.profit > 0;
      return metric;
    });

    return metrics.sort((a, b) => b.profit - a.profit);
  }, [transactions, costCenters, dateRange]);

  // Calculate summary metrics
  const summary = useMemo(() => {
    const activeProjects = projectMetrics.length;
    const profitableProjects = projectMetrics.filter((p) => p.isProfitable).length;
    const lossProjects = projectMetrics.filter((p) => !p.isProfitable && p.revenue > 0).length;
    const totalRevenue = projectMetrics.reduce((sum, p) => sum + p.revenue, 0);
    const totalExpenses = projectMetrics.reduce((sum, p) => sum + p.expenses, 0);
    const totalProfit = totalRevenue - totalExpenses;
    const totalProfitPercentage = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      activeProjects,
      profitableProjects,
      lossProjects,
      totalRevenue,
      totalExpenses,
      totalProfit,
      totalProfitPercentage,
    };
  }, [projectMetrics]);

  const isLoading = transactionsLoading || costCentersLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="section-delimited">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-primary" />
            Dashboard de Projetos
          </h1>
          <p className="text-muted-foreground">
            Health Check: acompanhe a saúde financeira dos seus projetos
          </p>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="section-delimited">
        <DatePickerWithRange
          date={dateRange}
          onDateChange={setDateRange}
        />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Projetos Ativos</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">{summary.activeProjects}</div>
            )}
            <p className="text-xs text-muted-foreground">
              Projetos com transações no período
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Projetos no Lucro</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-green-600">{summary.profitableProjects}</div>
            )}
            <p className="text-xs text-muted-foreground">
              {summary.activeProjects > 0 
                ? `${((summary.profitableProjects / summary.activeProjects) * 100).toFixed(1)}% dos projetos`
                : 'Nenhum projeto ativo'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Projetos em Prejuízo</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-red-600">{summary.lossProjects}</div>
            )}
            <p className="text-xs text-muted-foreground">
              {summary.activeProjects > 0 
                ? `${((summary.lossProjects / summary.activeProjects) * 100).toFixed(1)}% dos projetos`
                : 'Nenhum projeto ativo'
              }
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lucro Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <>
                <div className={`text-2xl font-bold ${summary.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(summary.totalProfit)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {summary.totalProfitPercentage.toFixed(1)}% sobre receita
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Project Details Table */}
      <div className="section-delimited">
        <Card>
          <CardHeader>
            <CardTitle>Detalhes por Projeto</CardTitle>
            <CardDescription>
              Receitas, despesas e lucro/prejuízo de cada projeto
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : projectMetrics.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum projeto com transações no período selecionado
              </div>
            ) : (
              <div className="space-y-4">
                {projectMetrics.map((project) => (
                  <div
                    key={project.costCenterId}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{project.costCenterName}</h3>
                        <Badge variant={project.isProfitable ? "default" : "destructive"}>
                          {project.isProfitable ? "Lucro" : "Prejuízo"}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Receitas</p>
                          <p className="font-medium text-green-600">
                            {formatCurrency(project.revenue)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Despesas</p>
                          <p className="font-medium text-red-600">
                            {formatCurrency(project.expenses)}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Lucro/Prejuízo</p>
                          <p className={`font-medium ${project.isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(project.profit)} ({project.profitPercentage.toFixed(1)}%)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

